import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { FirebaseContext } from '../../firebase';
import Button from '../elements/button/Button';
import InputText from '../forms/input/InputText';
import { PlayNotifications } from '../../redux/notifications/PlayNotifications';
import { preSlugifyMemory } from '../../utils/stringUtils';
import slugify from 'react-slugify';
import {
  RiCheckLine,
  RiCloseLine,
  RiLoader4Line,
  RiCheckboxCircleLine,
  RiCloseCircleLine,
} from 'react-icons/ri';
import {LOCALES_OBJECT} from "../../utils/constantsFront";

const PLANS = ['trial', 'fast', 'boost', 'one', 'custom'];
const LEVELS = [
  { value: 'level-1', label: 'Level 1 (entity)' },
  { value: 'level-2', label: 'Level 2 (meta)' },
];

const CreateProjectForm = ({ closeForm }) => {
  const { t } = useTranslation(['common', 'messages', 'dashboard']);
  const dispatch = useDispatch();
  const firebase = useContext(FirebaseContext);

  const [formLoading, setFormLoading] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [entityName, setEntityName] = useState('');
  const [entitySlug, setEntitySlug] = useState('');
  const [branch, setBranch] = useState('main');
  const [level, setLevel] = useState('level-1');
  const [plan, setPlan] = useState('trial');
  // Plan custom : crédits du plan et renouvellement mensuel, configurés par l'admin
  const [customCredits, setCustomCredits] = useState('');
  const [customMonthlyRenewal, setCustomMonthlyRenewal] = useState(false);
  const [mode, setMode] = useState('demo');
  const [locale, setLocale] = useState('fr_FR');
  const [deploySource, setDeploySource] = useState('cloudbuild');
  const [domainLoading, setDomainLoading] = useState(false);
  const [domainAvailable, setDomainAvailable] = useState(null);

  useEffect(() => {
    const slug = slugify(preSlugifyMemory(entityName));
    setEntitySlug(slug);
    setDomainAvailable(null);

    if (!entityName || entityName.length < 2) return;

    setDomainLoading(true);
    const timeout = setTimeout(() => verifyDomain(slug), 500);
    return () => clearTimeout(timeout);
  }, [entityName]);

  const verifyDomain = (slug) => {
    firebase.db.collection('reserved-subdomains').doc(slug).get().then((doc) => {
      if (doc.exists) {
        setDomainAvailable(false);
        setDomainLoading(false);
        return;
      }
      firebase.db.collection('projects').where('subdomain', '==', slug).get().then((snap) => {
        setDomainAvailable(snap.docs.length === 0);
        setDomainLoading(false);
      });
    });
  };

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const customCreditsNumber = Number(customCredits);
  const customCreditsInvalid = plan === 'custom' && (!Number.isInteger(customCreditsNumber) || customCreditsNumber <= 0);
  const canSubmit = entityName.length >= 2 && firstName.length >= 1 && lastName.length >= 1
    && isEmailValid && domainAvailable === true && !customCreditsInvalid && !formLoading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setFormLoading(true);

    try {
      const requestLocale = LOCALES_OBJECT.filter(item => item.code === locale).shift();

      const entityRequestRef = await firebase.db.collection('entity-request').add({
        entity_name: entityName,
        first_name: firstName,
        last_name: lastName,
        email,
        language_version: requestLocale.name || "english",
        branch: branch || 'main',
        status: 'request',
        deploy_source: deploySource,
        created_by: 'admin',
      });

      const deployFunction = firebase.functions.httpsCallable('deployTopicPub');
      const result = await deployFunction({
        entityName,
        entityRequestId: entityRequestRef.id,
        levelRequest: level,
        branch: branch || 'main',
        plan,
        ...(plan === 'custom' && {
          custom_plan: {
            credits: customCreditsNumber,
            monthly_renewal: customMonthlyRenewal,
          },
        }),
        mode,
        default_locale: locale,
        deploy_source: deploySource,
      });

      if (result.data?.success === true) {
        dispatch(PlayNotifications({
          display: true,
          theme: 'success',
          message: t('success.Deploy-request-success', { ns: 'messages' }),
        }));
        closeForm();
      } else {
        const msg = result.data?.message || 'Erreur';
        dispatch(PlayNotifications({ display: true, theme: 'danger', message: msg }));
        setFormLoading(false);
      }
    } catch (err) {
      dispatch(PlayNotifications({
        display: true,
        theme: 'danger',
        message: err?.message || 'Erreur',
      }));
      setFormLoading(false);
    }
  };

  return (
    <div className="fixed z-30 inset-0 flex justify-center items-center bg-gray-600 bg-opacity-50 overflow-y-auto h-full max-w-100vw w-full max-h-100vh py-10">
      <div className="relative p-5 pb-0 border shadow-lg rounded-md bg-white w-4/5 md:w-3/5 lg:w-2/5 max-h-full memoryScrollbar">

        <div className="flex items-center justify-between">
          <h2 className="text-lg">{t('projects.Create-project', { ns: 'dashboard', defaultValue: 'Créer un projet' })}</h2>
        </div>

        <div className="mt-3 flex flex-col gap-y-4">

          {/* Entity name + domain check */}
          <div>
            <InputText
              id="entityName"
              name="entityName"
              label={t('projects.form.entityName.label', { ns: 'dashboard', defaultValue: 'Nom du projet' })}
              placeHolder={t('projects.form.entityName.placeholder', { ns: 'dashboard', defaultValue: 'ex: Sciences Po' })}
              state={entityName}
              setstate={(e) => setEntityName(e.target.value)}
            />
            {entityName.length >= 2 && (
              <div className="flex items-center gap-1 mt-1 text-xs">
                {domainLoading ? (
                  <RiLoader4Line className="w-3 h-3 animate-spin text-gray-400" />
                ) : domainAvailable === true ? (
                  <RiCheckboxCircleLine className="w-3 h-3 text-green-500" />
                ) : domainAvailable === false ? (
                  <RiCloseCircleLine className="w-3 h-3 text-red-500" />
                ) : null}
                <span className={`${domainAvailable === false ? 'text-red-500' : 'text-gray-500'}`}>
                  {entitySlug}.my-memory.io
                  {domainAvailable === false && ' — sous-domaine indisponible'}
                </span>
              </div>
            )}
          </div>

          {/* First name / Last name */}
          <div className="flex gap-x-4">
            <InputText
              id="firstName"
              name="firstName"
              label={t('projects.form.firstName.label', { ns: 'dashboard', defaultValue: 'Prénom' })}
              placeHolder={t('projects.form.firstName.placeholder', { ns: 'dashboard', defaultValue: 'ex: Jean' })}
              state={firstName}
              setstate={(e) => setFirstName(e.target.value)}
            />
            <InputText
              id="lastName"
              name="lastName"
              label={t('projects.form.lastName.label', { ns: 'dashboard', defaultValue: 'Nom' })}
              placeHolder={t('projects.form.lastName.placeholder', { ns: 'dashboard', defaultValue: 'ex: Dupont' })}
              state={lastName}
              setstate={(e) => setLastName(e.target.value)}
            />
          </div>

          {/* Email */}
          <div>
            <InputText
              id="email"
              name="email"
              label={t('projects.form.email.label', { ns: 'dashboard', defaultValue: 'Email' })}
              placeHolder={t('projects.form.email.placeholder', { ns: 'dashboard', defaultValue: 'ex: contact@exemple.com' })}
              state={email}
              setstate={(e) => setEmail(e.target.value)}
            />
            {email.length > 0 && !isEmailValid && (
              <span className="text-xs text-red-500">
                {t('projects.form.email.invalid', { ns: 'dashboard', defaultValue: 'Email invalide' })}
              </span>
            )}
          </div>

          {/* Branch */}
          <div>
            <InputText
              id="branch"
              name="branch"
              label={t('projects.form.branch.label', { ns: 'dashboard', defaultValue: 'Branche Git' })}
              placeHolder="main"
              state={branch}
              setstate={(e) => setBranch(e.target.value)}
            />
          </div>

          {/* Level */}
          <div className="space-y-1">
            <span className="text-sm font-medium">{t('projects.form.level.label', { ns: 'dashboard', defaultValue: 'Niveau' })}</span>
            {LEVELS.map((l) => (
              <div key={l.value} className="flex items-center gap-2">
                <input type="radio" id={l.value} name="level" value={l.value}
                  checked={level === l.value} onChange={() => setLevel(l.value)} />
                <label htmlFor={l.value}>{l.label}</label>
              </div>
            ))}
          </div>

          {/* Plan */}
          <div className="space-y-1">
            <span className="text-sm font-medium">{t('Plan', { ns: 'common' })}</span>
            <div className="flex gap-4 flex-wrap">
              {PLANS.map((p) => (
                <div key={p} className="flex items-center gap-2">
                  <input type="radio" id={`plan-${p}`} name="plan" value={p}
                    checked={plan === p} onChange={() => setPlan(p)} />
                  <label htmlFor={`plan-${p}`} className="capitalize">{p}</label>
                </div>
              ))}
            </div>
          </div>

          {/* Plan custom : crédits + renouvellement mensuel */}
          {plan === 'custom' && (
            <div className="flex flex-col gap-y-3">
              <div>
                <InputText
                  id="custom_credits"
                  name="custom_credits"
                  type="number"
                  min={1}
                  label={t('projects.form.custom-plan.credits-label', { ns: 'dashboard' })}
                  placeHolder={t('projects.form.custom-plan.credits-placeholder', { ns: 'dashboard' })}
                  state={customCredits}
                  setstate={(e) => setCustomCredits(e.target.value)}
                />
                {customCreditsInvalid && (
                  <p className="mt-1 text-xs text-red-500">{t('projects.form.custom-plan.credits-invalid', { ns: 'dashboard' })}</p>
                )}
              </div>

              <div>
                <label htmlFor="custom_monthly_renewal" className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    id="custom_monthly_renewal"
                    type="checkbox"
                    checked={customMonthlyRenewal}
                    onChange={(e) => setCustomMonthlyRenewal(e.target.checked)}
                  />
                  {t('projects.form.custom-plan.monthly-renewal-label', { ns: 'dashboard' })}
                </label>
                <p className="mt-1 text-xs text-grayPrimary">
                  {t('projects.form.custom-plan.monthly-renewal-notice', { ns: 'dashboard' })}
                </p>
              </div>
            </div>
          )}

          {/* Mode */}
          <div className="space-y-1">
            <span className="text-sm font-medium">{t('Mode', { ns: 'common' })}</span>
            <div className="flex gap-4">
              {['demo', 'pro'].map((m) => (
                <div key={m} className="flex items-center gap-2">
                  <input type="radio" id={`mode-${m}`} name="mode" value={m}
                    checked={mode === m} onChange={() => setMode(m)} />
                  <label htmlFor={`mode-${m}`} className="capitalize">{m}</label>
                </div>
              ))}
            </div>
          </div>

          {/* Locale */}
          <div className="space-y-1">
            <span className="text-sm font-medium">{t('projects.form.locale.label', { ns: 'dashboard', defaultValue: 'Langue par défaut' })}</span>
            <div className="flex gap-4">
              {[{ value: 'fr_FR', label: 'Français' }, { value: 'en_GB', label: 'English' }].map((l) => (
                <div key={l.value} className="flex items-center gap-2">
                  <input type="radio" id={`locale-${l.value}`} name="locale" value={l.value}
                    checked={locale === l.value} onChange={() => setLocale(l.value)} />
                  <label htmlFor={`locale-${l.value}`}>{l.label}</label>
                </div>
              ))}
            </div>
          </div>

          {/* Deploy source */}
          <div className="space-y-1">
            <span className="text-sm font-medium">{t('projects.form.deploySource.label', { ns: 'dashboard', defaultValue: 'Moteur de déploiement backend' })}</span>
            <div className="flex gap-4">
              {[{ value: 'cloudbuild', label: 'Cloud Build' }, { value: 'gitlab', label: 'GitLab CI' }].map((s) => (
                <div key={s.value} className="flex items-center gap-2">
                  <input type="radio" id={`deploy-${s.value}`} name="deploySource" value={s.value}
                    checked={deploySource === s.value} onChange={() => setDeploySource(s.value)} />
                  <label htmlFor={`deploy-${s.value}`}>{s.label}</label>
                </div>
              ))}
            </div>
          </div>

        </div>

        <div className="flex items-end justify-between border-t border-gray-200 pb-5 pt-2 mt-6 sticky bottom-0 bg-white">
          <Button
            theme="simple"
            size="small"
            icon={<RiCloseLine className="w-4 h-4 mr-1" />}
            actionType="action"
            action={() => closeForm()}
          >
            {t('action.Back', { ns: 'common' })}
          </Button>

          <Button
            theme={canSubmit ? '' : 'disabled'}
            size="small"
            icon={formLoading
              ? <RiLoader4Line className="w-4 h-4 ml-1 animate-spin" />
              : <RiCheckLine className="w-4 h-4 ml-1" />}
            iconPosition="right"
            actionType="action"
            action={handleSubmit}
          >
            {t('action.Confirm', { ns: 'common' })}
          </Button>
        </div>

      </div>
    </div>
  );
};

export default CreateProjectForm;